import { DeliveryInfo, DeliveryInfoInput } from 'src/apollo/onlineOrdering'
import isEmpty from 'lodash/isEmpty'
import { CountryAddressConfig, FormatterOptions, ConfigOptions } from './types'

const dummyAddressValue = 'XX'

/**
 * Formatters
 */

/**
 * Default address formatters that work for most countries
 * Countries can override these or have their own custom formatting
 */
export const createDefaultFormatters = (options?: FormatterOptions) => {
  const address2Style = options?.address2Style ?? 'address2Last'

  const formatLine1 = (address: DeliveryInfo, includeAddress2 = true) => {
    if (!address) return ''

    if (!includeAddress2 || !address?.address2) {
      return address.address1
    }

    // Format based on style
    switch (address2Style) {
      case 'forwardSlash':
        return `${address.address2}/${address.address1}`
      case 'address2First':
        return `${address.address2}, ${address.address1}`
      case 'address2Last':
      default:
        return `${address.address1}, ${address.address2}`
    }
  }

  const formatLine2 = (address: DeliveryInfo, includeZip = true) => {
    if (!address) return ''

    const zipcode =
      address?.zipCode === dummyAddressValue || !includeZip
        ? ''
        : ` ${address?.zipCode}`

    // Use administrativeArea if available, otherwise use state
    // Only show state for US addresses (or when country is undefined/null, which defaults to US)
    const stateOrAdminArea =
      address.administrativeArea ||
      (address?.state !== dummyAddressValue &&
      (!address.country || address.country === 'US')
        ? address?.state
        : '')

    const stateDisplay = stateOrAdminArea ? ` ${stateOrAdminArea}` : ''

    return `${address?.city},${stateDisplay}${zipcode}`
  }

  return { formatLine1, formatLine2 }
}

/**
 * Config builder
 */

/**
 * Creates a standard address configuration with common defaults
 * This reduces duplication across country configs
 */
export const createStandardConfig = (
  options: ConfigOptions
): CountryAddressConfig => {
  const { fieldOverrides = {} } = options

  return {
    countryCode: options.countryCode,
    countryName: options.countryName,
    requiresStreetNumber: options.requiresStreetNumber ?? true,
    fields: {
      address1: {
        googleTypes: ['street_number', 'route', 'street_address', 'premise'],
        required: true,
        preferLongName: false,
        labelKey: 'address-config.street-address',
        errorKey: 'address-config.street-address-with-number-required',
        ...fieldOverrides.address1
      },
      address2: {
        googleTypes: ['subpremise'],
        required: false,
        preferLongName: true,
        labelKey: 'address-config.apt-suite-unit',
        ...fieldOverrides.address2
      },
      city: {
        googleTypes: [
          'locality',
          'postal_town',
          'sublocality_level_1',
          'administrative_area_level_2'
        ],
        required: true,
        preferLongName: true,
        labelKey: 'address-config.city',
        errorKey: 'address-config.city-required',
        ...fieldOverrides.city
      },
      stateOrProvince: {
        googleTypes: ['administrative_area_level_1'],
        required: true,
        preferLongName: false,
        labelKey: 'address-config.state',
        errorKey: 'address-config.state-required',
        ...fieldOverrides.stateOrProvince
      },
      postalCode: {
        googleTypes: ['postal_code'],
        required: true,
        preferLongName: false,
        labelKey: 'address-config.zip-code',
        errorKey: 'address-config.zip-code-required',
        ...fieldOverrides.postalCode
      }
    },
    formatters: options.formatters,
    addressPrecision: options.addressPrecision ?? 'street'
  }
}

/**
 * Validation
 */
export const getMissingRequiredFieldErrorsUtil = (
  address: DeliveryInfoInput | null,
  getCountryConfig: (code?: string | null) => CountryAddressConfig,
  countryCode?: string | null
): string[] => {
  const defaultError = 'address-search-screen.trouble-verifying-address'

  // If address is null or empty, return generic error
  if (!address || isEmpty(address)) {
    return [defaultError]
  }

  const config = getCountryConfig(countryCode)
  const missing: string[] = []

  // Check for field-specific errors
  if (config.fields.address1.required && !address.address1) {
    missing.push(config.fields.address1?.errorKey ?? defaultError)
  }
  if (config.fields.address2.required && !address.address2) {
    missing.push(config.fields.address2?.errorKey ?? defaultError)
  }
  if (config.fields.city.required && !address.city) {
    missing.push(config.fields.city?.errorKey ?? defaultError)
  }
  if (
    config.fields.postalCode.required &&
    (!address.zipCode || address.zipCode === dummyAddressValue)
  ) {
    missing.push(config.fields.postalCode?.errorKey ?? defaultError)
  }

  return missing
}

/**
 * Normalization
 */
export const normalizeGoogleAddressUtil = (
  addressFromGoogle: google.maps.places.PlaceResult,
  getCountryConfig: (code?: string | null) => CountryAddressConfig,
  rxCountry?: string | null
) => {
  const normalizedAddress = {} as DeliveryInfoInput

  if (!addressFromGoogle.address_components) {
    return null
  }

  const countryComponent = addressFromGoogle.address_components.find((comp) =>
    comp.types.includes('country')
  )
  normalizedAddress.country = countryComponent?.short_name ?? null
  const countryConfig = getCountryConfig(rxCountry ?? normalizedAddress.country)

  // prefer a route or street_address over a premise
  const hasStreetAddress = addressFromGoogle.address_components.some(
    (comp) =>
      comp.types.includes('route') || comp.types.includes('street_address')
  )

  const address1Parts: string[] = []

  for (let component of addressFromGoogle.address_components) {
    for (let componentType of component.types) {
      if (countryConfig.fields.address1.googleTypes.includes(componentType)) {
        // Skip premise if we have a street address
        if (componentType === 'premise' && hasStreetAddress) {
          continue
        }

        const value = countryConfig.fields.address1.preferLongName
          ? component.long_name
          : component.short_name

        // street_number should go first
        if (componentType === 'street_number') {
          address1Parts.unshift(value)
        } else {
          address1Parts.push(value)
        }
        break
      }

      if (
        countryConfig.fields.address2 &&
        countryConfig.fields.address2.googleTypes.includes(componentType) &&
        !normalizedAddress.address2
      ) {
        let value = countryConfig.fields.address2.preferLongName
          ? component.long_name
          : component.short_name

        normalizedAddress.address2 = value
        break
      }

      if (
        countryConfig.fields.city.googleTypes.includes(componentType) &&
        !normalizedAddress.city
      ) {
        normalizedAddress.city = countryConfig.fields.city.preferLongName
          ? component.long_name
          : component.short_name
        break
      }

      if (
        countryConfig.fields.stateOrProvince &&
        countryConfig.fields.stateOrProvince.googleTypes.includes(
          componentType
        ) &&
        !normalizedAddress.state
      ) {
        const value = countryConfig.fields.stateOrProvince.preferLongName
          ? component.long_name
          : component.short_name

        normalizedAddress.state = value
        normalizedAddress.administrativeArea = value
        break
      }

      if (
        countryConfig.fields.postalCode.googleTypes.includes(componentType) &&
        !normalizedAddress.zipCode
      ) {
        normalizedAddress.zipCode = countryConfig.fields.postalCode
          .preferLongName
          ? component.long_name
          : component.short_name
        break
      }
    }
  }

  // Merge address1 parts into final address1 string
  if (address1Parts.length > 0) {
    normalizedAddress.address1 = address1Parts.join(' ')
  }

  // Add lat/lng if available
  if (addressFromGoogle.geometry?.location) {
    normalizedAddress.latitude = addressFromGoogle.geometry.location.lat()
    normalizedAddress.longitude = addressFromGoogle.geometry.location.lng()
  }

  if (normalizedAddress.country && normalizedAddress.country !== 'US') {
    // state is not a relevant field for international countries, so we default to the dummy state value 'XX'
    normalizedAddress.state = dummyAddressValue
    if (isEmpty(normalizedAddress.zipCode)) {
      // zipcode is not always found for UKI addresses, so we default to a dummy value 'XX'
      normalizedAddress.zipCode = dummyAddressValue
    }
  }
  return isEmpty(normalizedAddress) ? null : normalizedAddress
}

export const formatDeliveryAddressUtil = (
  address: DeliveryInfo | null,
  options: {
    full?: boolean
    includeAddress2?: boolean
    includeZip?: boolean
    country?: string | null
  } = {},
  getCountryConfig: (code?: string | null) => CountryAddressConfig
) => {
  if (!address) {
    return ''
  }

  const config = getCountryConfig(options.country ?? 'US')
  const optionsWithDefaults = {
    includeAddress2: true,
    includeZip: true,
    ...options
  }

  const line2 = options.full
    ? `, ${config.formatters.formatLine2(
        address,
        optionsWithDefaults.includeZip
      )}`
    : ''
  return `${config.formatters.formatLine1(
    address,
    optionsWithDefaults.includeAddress2
  )}${line2}`
}
